import React, { useEffect, useState } from 'react'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    virksomhed: '',
    destination: '',
    antalRejsende: '2',
    oensketAfrejse: '',
    besked: '',
    website_url: '', // Mandatory Honeypot field
  })

  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    document.title = 'Kontakt SKI & CO TRAVEL - SKI & CO TRAVEL ApS'
  }, [])

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const payload = {
        navn: formData.navn,
        telefonnummer: formData.telefon,
        email: formData.email,
        virksomhed: formData.virksomhed,
        destination: formData.destination,
        antal_rejsende: Number(formData.antalRejsende) || 2,
        oensket_afrejse: formData.oensketAfrejse,
        beskrivelse: formData.besked,
        website_url: formData.website_url, // Honeypot
        modtaget_dato: new Date().toISOString().split('T')[0],
      }

      await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      setSubmitted(true)
    } catch (err) {
      console.warn('Webform notice:', err)
      setSubmitted(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-12">
      {/* Page Header */}
      <div className="space-y-3 max-w-3xl">
        <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
          SKI & CO TRAVEL ApS
        </span>
        <h1 className="font-heading font-extrabold text-2xl sm:text-4xl text-white">
          Kontakt Os & Send Forespørgsel
        </h1>
        <p className="text-slate-300 text-base leading-relaxed">
          Vi står klar til at hjælpe dig med at sammensætte den ideelle skiferie. Kontakt os direkte på telefon eller e-mail, eller udfyld formularen.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Contact Information Card */}
        <div className="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-3xl p-8 space-y-8 shadow-2xl h-fit">
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-cyan-400">
              Direkte kontakt
            </span>
            <h2 className="font-heading font-extrabold text-xl text-white">
              SKI & CO TRAVEL ApS
            </h2>
            <p className="text-slate-400 text-xs">
              Personlig og professionel skirejserådgivning
            </p>
          </div>

          <div className="space-y-6 text-sm">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center shrink-0 text-base">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase text-slate-400 block mb-0.5">
                  Adresse
                </span>
                <span className="text-white font-medium">
                  Kærmindevej 31, 6000 Kolding
                </span>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center shrink-0 text-base">
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase text-slate-400 block mb-0.5">
                  Telefon
                </span>
                <a
                  href="tel:+4551589452"
                  className="text-white font-bold text-lg hover:text-cyan-300 transition-colors whitespace-nowrap block"
                >
                  +45 51 58 94 52
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center shrink-0 text-base">
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase text-slate-400 block mb-0.5">
                  E-mail
                </span>
                <a
                  href="mailto:claus@skiogco.dk"
                  className="text-white font-bold text-base hover:text-cyan-300 transition-colors whitespace-nowrap block"
                >
                  claus@skiogco.dk
                </a>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-800">
            <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-1">
              <span className="text-xs font-bold text-cyan-400 block">
                Åbningstider & Telefontid
              </span>
              <p className="text-xs text-slate-300">
                Vi besvarer opkald og e-mails alle hverdage. Har du et hastende spørgsmål vedrørende en rejse, fanger du os direkte på mobilen.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-8 sm:p-10 shadow-2xl space-y-6">
          <div className="space-y-1">
            <h2 className="font-heading font-extrabold text-2xl text-white">
              Send en skræddersyet forespørgsel
            </h2>
            <p className="text-slate-400 text-xs">
              Udfyld felterne nedenfor, så vender Claus hurtigt tilbage med et uforpligtende tilbud.
            </p>
          </div>

          {submitted ? (
            <div className="bg-emerald-950/40 border border-emerald-800 p-8 rounded-2xl text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center mx-auto text-xl">
                <i className="fa-solid fa-check" aria-hidden="true" />
              </div>
              <h3 className="font-heading font-bold text-xl text-white">
                Mange tak for din besked!
              </h3>
              <p className="text-slate-300 text-sm max-w-md mx-auto leading-relaxed">
                Vi har modtaget dine oplysninger og kontakter dig snarest muligt. Du er også altid velkommen til at ringe på <a href="tel:+4551589452" className="text-cyan-400 font-bold whitespace-nowrap">+45 51 58 94 52</a>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 text-sm">
              {/* Mandatory Honeypot field (SR ONLY) */}
              <input
                type="text"
                name="website_url"
                value={formData.website_url}
                onChange={handleChange}
                className="sr-only"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Navn <span className="text-cyan-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    name="navn"
                    value={formData.navn}
                    onChange={handleChange}
                    placeholder="Dit fulde navn"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Telefonnummer <span className="text-cyan-400">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    name="telefon"
                    value={formData.telefon}
                    onChange={handleChange}
                    placeholder="+45 12 34 56 78"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    E-mailadresse <span className="text-cyan-400">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="din@email.dk"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Virksomhed / Gruppe (valgfri)
                  </label>
                  <input
                    type="text"
                    name="virksomhed"
                    value={formData.virksomhed}
                    onChange={handleChange}
                    placeholder="Evt. firmanavn"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Ønsket destination
                  </label>
                  <select
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:border-cyan-400"
                  >
                    <option value="">Vælg destination...</option>
                    <option value="St. Anton am Arlberg">St. Anton am Arlberg</option>
                    <option value="Saalbach Hinterglemm">Saalbach Hinterglemm</option>
                    <option value="Val Gardena / Dolomitterne">Val Gardena / Dolomitterne</option>
                    <option value="Val Thorens">Val Thorens</option>
                    <option value="Hemsedal">Hemsedal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Antal rejsende
                  </label>
                  <input
                    type="number"
                    min="1"
                    name="antalRejsende"
                    value={formData.antalRejsende}
                    onChange={handleChange}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Ønsket afrejse
                  </label>
                  <input
                    type="text"
                    name="oensketAfrejse"
                    value={formData.oensketAfrejse}
                    onChange={handleChange}
                    placeholder="F.eks. Uge 7"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Særlige ønsker eller kommentarer
                </label>
                <textarea
                  rows="4"
                  name="besked"
                  value={formData.besked}
                  onChange={handleChange}
                  placeholder="Fortæl om jeres ønsker til skikurser, hotelkomfort, transportform m.m."
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-base transition-colors shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin" aria-hidden="true" />
                    <span>Sender besked...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                    <span>Send uforpligtende forespørgsel</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
